import {
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import {PendingUnderwritingRequirementDto} from 'Agent/Policies/api';
import 'Agent/Policies/components/PendingUnderwritingRequirements.sass';
import {fetchPendingUnderwritingRequirements} from 'Agent/Policies/ducks/policy';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import classnames from 'classnames';
import {theme} from 'Core/components/WithTheme';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {fromResult} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import {constant} from 'fp-ts/lib/function';
import * as NEA from 'fp-ts/lib/NonEmptyArray';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useRouteMatch} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import {Column} from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {toMoneyValue} from 'Shared/utilities/FormatMoney';
import {prop} from 'Shared/utilities/prop';
import {selectCurrentAgentNumbers} from 'Users/selectors/userProfileSelectors';

const PendingUnderwritingRequirements = ({selectedAgency}: WithSelectedAgencyProps) => {
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const match = useRouteMatch();
  useBreadcrumbs('Pending Underwriting Requirements', match.url);
  const agentNumbers = useSelector((state: ReducerState) => selectCurrentAgentNumbers(state));
  const {pendingUnderwritingRequirements} = useSelector((state: ReducerState) => state.policy);

  const [showDetails, setShowDetails] = useState(false);
  const [currentDetails, setCurrentDetails] = useState('');

  useEffect(() => {
    dispatch(
      fetchPendingUnderwritingRequirements(
        selectedAgency.encryptedIdentifier,
        agentNumbers.map(prop('value')),
        pipe(agentNumbers, NEA.fromArray, O.map(NEA.head), O.fold(constant(false), prop('isNewYork')))
      )
    );
  }, [agentNumbers]);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: 'PendingUnderwritingRequirements',
    relativeFileLocation: `api/policies/v1/underwritingrequirement/pending/summary${addFilterParameters({
      isNewYork: selectedAgency.isNewYork,
      encryptedIdentifier: selectedAgency.encryptedIdentifier,
      exportToCsv: true,
    }) +
      agentNumbers
        .map(prop('value'))
        .map(an => `&agentNumbers=${an}`)
        .join('')}`,
    children: 'Export To CSV',
  };

  return renderAsyncView(pendingUnderwritingRequirements, result => {
    const pagedPendingUnderwritingRequirements: Page<PendingUnderwritingRequirementDto> = {
      currentPageNumber: 0,
      links: [],
      result,
      totalCount: result.length,
    };

    return (
      <>
        <Paper
          className={classnames('pending-underwriting-requirement__notes', {
            'pending-underwriting-requirement--desktop__notes': isDesktop,
          })}
        >
          <Grid container spacing={2} direction="column">
            <Grid item>
              <Typography variant="body1">
                Please note file will be withdrawn after 30 days from the date of request if requirement has not been
                received. Click on the policy number for additional options and information.
              </Typography>
              <Typography variant="body1">
                Please use the following fax number to submit pending underwriting requirements:
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1">{'    1-866-428-2513'}</Typography>
            </Grid>
          </Grid>
        </Paper>
        <PagedView
          pagedViewKey="pendingUnderwritingRequirements"
          data={fromResult(pagedPendingUnderwritingRequirements)}
          loadMore={() => {}}
          actions={
            isMobile ? (
              <FileDownloadFab variant="extended" color="primary" displayTooltip {...exportToCsvProps} />
            ) : (
              <FileDownloadButton variant="contained" color="primary" displayTooltip {...exportToCsvProps} />
            )
          }
        >
          {columnProps => (
            <>
              <Column {...columnProps} title="Account Name" default ddprivacy="mask">
                {pendingUW => pendingUW.accountName}
              </Column>
              <Column {...columnProps} title="Account Number" default ddprivacy="mask">
                {pendingUW => pendingUW.accountNumber}
              </Column>
              <Column {...columnProps} title="Insured Name" default ddprivacy="mask">
                {pendingUW => pendingUW.insuredName}
              </Column>
              <Column {...columnProps} title="Policy Number" default ddprivacy="mask">
                {pendingUW => (
                  <Link to={PolicyRouteConstants.POLICIES_BY_POLICY_NUMBER(pendingUW.policyNumber!)}>
                    <Button color="primary">
                      <Typography variant="body2">{pendingUW.policyNumber}</Typography>
                    </Button>
                  </Link>
                )}
              </Column>
              <Column {...columnProps} title="Plan" default>
                {pendingUW => pendingUW.plan}
              </Column>
              <Column {...columnProps} title="Monthly Premium" default>
                {pendingUW => toMoneyValue(pendingUW.premium)}
              </Column>
              <Column {...columnProps} title="Date Requested" default>
                {pendingUW => toFormattedDateAsUTC(pendingUW.dateRequested)}
              </Column>
              <Column {...columnProps} title="Date Withdrawn" default>
                {pendingUW => toFormattedDateAsUTC(pendingUW.statusDate)}
              </Column>
              <Column {...columnProps} title="Requirements" default>
                {pendingUW => pendingUW.groupName}
              </Column>
              <Column {...columnProps} isActions title="Actions" default>
                {pendingUW => (
                  <Button
                    color="primary"
                    onClick={() => {
                      setShowDetails(true);
                      setCurrentDetails(pendingUW.details || '');
                    }}
                  >
                    View Details
                  </Button>
                )}
              </Column>
            </>
          )}
        </PagedView>
        <Dialog open={showDetails} onClose={() => setShowDetails(false)}>
          <DialogTitle id="dialog-title">Pending Underwriting Requirement Details</DialogTitle>
          <DialogContent>
            <DialogContentText id="dialog-description">{currentDetails}</DialogContentText>
          </DialogContent>
        </Dialog>
      </>
    );
  });
};

export default withSelectedAgency(PendingUnderwritingRequirements);
